(() => {
  const elementName = "product-hotspot-popover";
  const stylesId = `${elementName}-styles`;

  if (customElements.get(elementName)) return;

  class ProductHotspotPopover extends HTMLElement {
    constructor() {
      super();

      this.productRequests = new Map();
      this.activeTrigger = null;
      this.pausedSwiper = null;
      this.mobileCardRequestId = 0;
    }

    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const loadId = Symbol("product-load");
      this.loadId = loadId;
      this.hidden = true;
      this.setAttribute("aria-busy", "true");

      this.injectStyles();
      this.ensurePositioningContext();
      this.triggers = Array.from(this.querySelectorAll(".trigger"));
      const productRequests = [];

      this.triggers.forEach((trigger) => {
        const handle = trigger.dataset.productHandle;

        if (!handle) return;

        trigger.setAttribute("role", "button");
        trigger.setAttribute("tabindex", "0");
        trigger.setAttribute("aria-label", `View ${handle.replaceAll("-", " ")}`);

        trigger.addEventListener(
          "mouseenter",
          () => {
            if (window.innerWidth >= 850) {
              this.showProductCard(trigger);
            }
          },
          { signal: this.abortController.signal },
        );
        trigger.addEventListener(
          "click",
          (event) => {

            if (window.innerWidth < 850) {
              this.openMobileProductModal(trigger);
              return;
            }

            this.classList.add("is-active");
            this.showProductCard(trigger);
          },
          { signal: this.abortController.signal },
        );
        trigger.addEventListener(
          "keydown",
          (event) => {
            if (event.key !== "Enter" && event.key !== " ") return;

            event.preventDefault();

            if (window.innerWidth < 850) {
              this.openMobileProductModal(trigger);
              return;
            }

            this.classList.add("is-active");
            this.showProductCard(trigger);
          },
          { signal: this.abortController.signal },
        );

        productRequests.push(this.getProduct(handle));
      });

      Promise.allSettled(productRequests).then(() => {
        if (!this.isConnected || this.loadId !== loadId) return;

        this.hidden = false;
        this.removeAttribute("aria-busy");
      });

      this.addEventListener("mouseleave", () => this.handleMouseLeave(), {
        signal: this.abortController.signal,
      });
      this.addEventListener("mouseenter", () => this.pauseParentSwiperAutoplay(), {
        signal: this.abortController.signal,
      });
    }

    disconnectedCallback() {
      this.closeMobileProductModal();
      this.resumeParentSwiperAutoplay();
      this.abortController?.abort();
      this.abortController = null;
      this.loadId = null;
      this.activeTrigger = null;
      this.removeProductCards();
    }

    injectStyles() {
      if (document.getElementById(stylesId)) return;

      const style = document.createElement("style");
      style.id = stylesId;
      style.textContent = `
        product-hotspot-popover {
          position: absolute;
          inset: 0;
        }

        product-hotspot-popover[hidden] {
          display: none;
        }

        product-hotspot-popover .trigger {
          position: absolute;
          display: none;
        }

        product-hotspot-popover:hover .trigger,
        product-hotspot-popover.is-active .trigger {
          display: block;
        }

        product-hotspot-popover .product-hotspot-card {
          position: absolute;
          top: 50%;
          left: 100%;
          transform: translateY(-50%);
        }

        .product-hotspot-card__close {
          position: absolute;
          top: 2%;
          right: 0;
          z-index: 1;
        }

        .product-hotspot-mobile-modal {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: flex-end;
        }

        .product-hotspot-mobile-modal__overlay {
          position: absolute;
          inset: 0;
          padding: 0;
          border: 0;
          background: rgba(0, 0, 0, 0.4);
        }

        .product-hotspot-mobile-modal__content {
          position: relative;
          width: 100%;
          transform: translateY(100%);
          transition: transform 0.3s ease;
        }

        .product-hotspot-mobile-modal.is-open .product-hotspot-mobile-modal__content {
          transform: translateY(0);
        }

        @media (max-width: 849.98px) {
          product-hotspot-popover .trigger {
            display: block;
          }
        }
      `;
      document.head.appendChild(style);
    }

    ensurePositioningContext() {
      const parent = this.parentElement;

      if (parent && window.getComputedStyle(parent).position === "static") {
        parent.style.position = "relative";
      }
    }

    async getProduct(handle) {
      if (!this.productRequests.has(handle)) {
        const request = fetch(`${window.location.origin}/products/${encodeURIComponent(handle)}.js`)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Unable to load product "${handle}"`);
            }

            return response.json();
          })
          .catch((error) => {
            this.productRequests.delete(handle);
            throw error;
          });

        this.productRequests.set(handle, request);
      }

      return this.productRequests.get(handle);
    }

    async showProductCard(trigger) {
      if (this.activeTrigger === trigger && trigger.querySelector(".product-hotspot-card")) {
        return;
      }

      this.removeProductCards();
      this.activeTrigger = trigger;

      try {
        const product = await this.getProduct(trigger.dataset.productHandle);

        if (this.activeTrigger !== trigger || !trigger.isConnected) return;

        trigger.appendChild(
          this.createProductCard(product, () => {
            this.classList.remove("is-active");
            this.activeTrigger = null;
            this.removeProductCards();
          }),
        );
      } catch (error) {
        console.error(error);
      }
    }

    async openMobileProductModal(trigger) {
      const handle = trigger.dataset.productHandle;

      if (!handle) return;

      this.closeMobileProductModal();

      const modal = document.createElement("div");
      modal.className = "product-hotspot-mobile-modal";
      modal.setAttribute("role", "dialog");
      modal.setAttribute("aria-modal", "true");
      modal.setAttribute("aria-label", "Product details");

      const overlay = document.createElement("button");
      overlay.type = "button";
      overlay.className = "product-hotspot-mobile-modal__overlay";
      overlay.setAttribute("aria-label", "Close product details");

      const content = document.createElement("div");
      content.className = "product-hotspot-mobile-modal__content";

      overlay.addEventListener("click", () => this.closeMobileProductModal());
      modal.append(overlay, content);

      this.mobileModal = modal;
      this.mobileModalContent = content;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      document.body.appendChild(modal);
      requestAnimationFrame(() => modal.classList.add("is-open"));

      const requestId = ++this.mobileCardRequestId;

      try {
        const product = await this.getProduct(handle);

        if (
          requestId !== this.mobileCardRequestId ||
          modal !== this.mobileModal ||
          !content.isConnected
        ) {
          return;
        }

        content.replaceChildren(
          this.createProductCard(product, () => this.closeMobileProductModal()),
        );
      } catch (error) {
        console.error(error);
        this.closeMobileProductModal();
      }
    }

    closeMobileProductModal() {
      if (!this.mobileModal) return;

      this.mobileCardRequestId += 1;
      this.mobileModal.remove();
      document.body.style.overflow = this.previousBodyOverflow || "";
      this.mobileModal = null;
      this.mobileModalContent = null;
      this.previousBodyOverflow = null;
    }

    createProductCard(product, closeHandler) {
      const card = document.createElement("div");
      card.className = "product-hotspot-card";

      const closeButton = document.createElement("button");
      closeButton.type = "button";
      closeButton.className = "product-hotspot-card__close";
      closeButton.setAttribute("aria-label", "Close product card");
      closeButton.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>
        </svg>
      `;
      closeButton.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        closeHandler?.();
      });

      const content = document.createElement("div");
      content.className = "product-hotspot-card__content";

      const imageWrapper = document.createElement("a");
      imageWrapper.className = "product-hotspot-card__imgwrapper";
      imageWrapper.href = product.url;

      const image = document.createElement("img");
      image.className = "product-hotspot-card__image";
      image.src = product.featured_image;
      image.alt = product.title;

      const title = document.createElement("a");
      title.className = "product-hotspot-card__title";
      title.textContent = product.title;
      title.href = product.url;

      const prices = document.createElement("div");
      prices.className = "product-hotspot-card__prices";

      let discount = null;

      const price = document.createElement("span");
      price.className = "product-hotspot-card__price";
      price.textContent = this.formatMoney(product.price);
      prices.appendChild(price);

      if (product.compare_at_price && product.compare_at_price > product.price) {
        const discountPercentage = Math.round(
          ((product.compare_at_price - product.price) / product.compare_at_price) * 100,
        );

        discount = document.createElement("div");
        discount.className = "product-hotspot-card__discount";
        discount.textContent = `${discountPercentage}%`;

        const comparePrice = document.createElement("s");
        comparePrice.className = "product-hotspot-card__compare-price";
        comparePrice.textContent = this.formatMoney(product.compare_at_price);
        prices.appendChild(comparePrice);
      } else {
        discount = document.createElement("div");
        discount.className = "product-hotspot-card__discount new-tag";
        discount.textContent = `NEW`;
      }

      const buttonWrapper = document.createElement("div");
      buttonWrapper.className = "product-hotspot-card__buttons";

      const buyNowButton = document.createElement("button");
      buyNowButton.type = "button";
      buyNowButton.className = "product-hotspot-card__buy-now";
      buyNowButton.ariaLabel = "BUY NOW";
      buyNowButton.dataset.id = product.variants?.[0]?.id;
      buyNowButton.textContent = "BUY NOW";
      buyNowButton.addEventListener("click", async () => {
        try {
          await fetch("/cart/clear.js", { method: "POST" });
          await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: product.variants?.[0]?.id, quantity: 1 }),
          });
          window.location.href = "/checkout";
        } catch (err) {
          console.log("加购失败，请重试");
        }
      });

      const addToCartButton = document.createElement("multi-add-cart-button");
      addToCartButton.type = "button";
      addToCartButton.className = "product-hotspot-card__add-to-cart";
      addToCartButton.setAttribute("aria-label", "Add to cart");
      addToCartButton.dataset.products = JSON.stringify([
        {
          variantId: product.variants?.[0]?.id,
          quantity: 1,
        },
      ]);
      addToCartButton.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none">
          <path d="M7.24264 8.23888L4 4.99624L5.41421 3.58203L8.65685 6.82467H23.8986C24.4509 6.82467 24.8986 7.27239 24.8986 7.82467C24.8986 7.92199 24.8844 8.0188 24.8565 8.11202L22.4565 16.112C22.3296 16.535 21.9402 16.8247 21.4986 16.8247H9.24264V18.8247H20.2427V20.8247H8.24264C7.69035 20.8247 7.24264 20.3769 7.24264 19.8247V8.23888ZM8.74264 24.8247C7.91421 24.8247 7.24264 24.1531 7.24264 23.3247C7.24264 22.4962 7.91421 21.8247 8.74264 21.8247C9.57107 21.8247 10.2426 22.4962 10.2426 23.3247C10.2426 24.1531 9.57107 24.8247 8.74264 24.8247ZM20.7427 24.8247C19.9142 24.8247 19.2427 24.1531 19.2427 23.3247C19.2427 22.4962 19.9142 21.8247 20.7427 21.8247C21.5711 21.8247 22.2427 22.4962 22.2427 23.3247C22.2427 24.1531 21.5711 24.8247 20.7427 24.8247Z" fill="black"></path>
        </svg>
      `;

      imageWrapper.append(image);
      buttonWrapper.append(addToCartButton, buyNowButton);
      content.append(title, prices, buttonWrapper);
      card.append(closeButton, imageWrapper, content);

      if (discount) {
        card.append(discount);
      }

      return card;
    }

    formatMoney(cents) {
      if (window.theme?.Currency?.formatMoney) {
        return window.theme.Currency.formatMoney(cents, window.theme.strings?.moneyFormat || "${{amount}}");
      }

      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(cents / 100);
    }

    removeProductCards() {
      this.querySelectorAll(".product-hotspot-card").forEach((card) => card.remove());
    }

    pauseParentSwiperAutoplay() {
      const swiperContainer = this.closest(".swiper-container");
      const swiper = swiperContainer?.swiper;

      if (!swiper || swiper.destroyed || !swiper.autoplay?.running) {
        return;
      }

      swiper.autoplay.stop();
      this.pausedSwiper = swiper;
    }

    resumeParentSwiperAutoplay() {
      const swiper = this.pausedSwiper;
      this.pausedSwiper = null;

      if (!swiper || swiper.destroyed) return;

      swiper.autoplay?.start();
    }

    handleMouseLeave() {
      this.resumeParentSwiperAutoplay();
      this.classList.remove("is-active");
      this.activeTrigger = null;
      this.removeProductCards();
    }
  }

  customElements.define(elementName, ProductHotspotPopover);
})();
